import * as THREE from 'three';
import type { Venue, VenueBlock, VenueLevel } from '../types';

/**
 * The 3D floor model, built once and driven by whichever renderer the platform
 * supplies — `THREE.WebGLRenderer` on the web, expo-three's GL renderer on
 * native. Geometry, materials, lighting, camera rig and hit-testing are
 * identical on both, so the hall looks the same pixel for pixel.
 *
 * The platform layer owns: creating the renderer, the animation loop, pointer
 * events, and drawing the zone labels (DOM on web, <Text> on native).
 */

export interface SceneColors {
  bg: number;
  floor: number;
  accent: number;
  accentLight: number;
  accentDeep: number;
  edge: number;
  gridMajor: number;
  gridMinor: number;
  outline: number;
  hemi: number;
  key: number;
}

export const defaultSceneColors: SceneColors = {
  bg: 0x161826,
  floor: 0x1d1f2c,
  accent: 0x9184d9,
  accentLight: 0xd2cefd,
  accentDeep: 0x2b2741,
  edge: 0x75798c,
  gridMajor: 0x2b2d3b,
  gridMinor: 0x232532,
  outline: 0x595d6c,
  hemi: 0xb5abfc,
  key: 0xe9e9ed,
};

export interface ZoneLabel {
  key: string;
  text: string;
  position: THREE.Vector3;
}

export interface ProjectedLabel {
  key: string;
  text: string;
  x: number;
  y: number;
  visible: boolean;
}

export interface CameraRig {
  az: number;
  pol: number;
  dist: number;
}

export const initialRig: CameraRig = { az: -0.5, pol: 0.72, dist: 34 };
export const DIST_MIN = 20;
export const DIST_MAX = 62;
export const POL_MIN = 0.28;
export const POL_MAX = 1.35;

export class VenueScene {
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly world: THREE.Group;
  readonly raycaster = new THREE.Raycaster();
  rig: CameraRig = { ...initialRig };

  private colors: SceneColors;
  private venue: Venue;
  private meshes: THREE.Mesh[] = [];
  private edges = new WeakMap<THREE.Mesh, THREE.LineSegments>();
  private selected: THREE.Mesh | null = null;
  private highlight = '';
  zoneLabels: ZoneLabel[] = [];

  constructor(venue: Venue, colors: SceneColors = defaultSceneColors) {
    this.venue = venue;
    this.colors = colors;

    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.Fog(colors.bg, 42, 110);

    this.camera = new THREE.PerspectiveCamera(40, 1, 0.1, 300);

    this.scene.add(new THREE.HemisphereLight(colors.hemi, colors.bg, 0.55));
    const key = new THREE.DirectionalLight(colors.key, 1.05);
    key.position.set(-9, 16, 8);
    this.scene.add(key);
    const rim = new THREE.DirectionalLight(colors.accent, 0.65);
    rim.position.set(10, 6, -10);
    this.scene.add(rim);

    this.world = new THREE.Group();
    this.scene.add(this.world);
  }

  /** Rebuilds the floor for a level. Safe to call on every level switch. */
  build(level: VenueLevel) {
    while (this.world.children.length) {
      const child = this.world.children[0];
      this.world.remove(child);
      disposeDeep(child);
    }
    this.meshes = [];
    this.selected = null;

    const c = this.colors;
    const p = level.plate;

    const plateGeo = new THREE.BoxGeometry(p.w, 0.35, p.d);
    const plate = new THREE.Mesh(
      plateGeo,
      new THREE.MeshStandardMaterial({ color: c.floor, roughness: 0.95, metalness: 0 }),
    );
    plate.position.set(p.x, -0.18, p.z);
    this.world.add(plate);

    const grid = new THREE.GridHelper(p.w, p.w, c.gridMajor, c.gridMinor);
    grid.position.set(p.x, 0.01, p.z);
    this.world.add(grid);

    const outline = new THREE.LineSegments(
      new THREE.EdgesGeometry(plateGeo),
      new THREE.LineBasicMaterial({ color: c.outline }),
    );
    outline.position.copy(plate.position);
    this.world.add(outline);

    for (const b of level.blocks) {
      const zoneColor = this.venue.zones[b.zone]?.color ?? c.edge;
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(b.w, b.h, b.d),
        new THREE.MeshStandardMaterial({ color: zoneColor, roughness: 0.72, metalness: 0.05 }),
      );
      mesh.position.set(b.x + b.w / 2, b.h / 2, b.z + b.d / 2);
      mesh.userData = b;
      this.world.add(mesh);

      const edge = new THREE.LineSegments(
        new THREE.EdgesGeometry(mesh.geometry),
        new THREE.LineBasicMaterial({ color: c.edge, transparent: true, opacity: 0.55 }),
      );
      edge.position.copy(mesh.position);
      this.world.add(edge);
      this.edges.set(mesh, edge);
      this.meshes.push(mesh);
    }

    // One label per zone, at the centroid of its blocks.
    const centroids: Record<string, { n: number; x: number; z: number }> = {};
    for (const b of level.blocks) {
      const acc = centroids[b.zone] ?? (centroids[b.zone] = { n: 0, x: 0, z: 0 });
      acc.n++; acc.x += b.x; acc.z += b.z;
    }
    this.zoneLabels = Object.entries(centroids).map(([key, v]) => ({
      key,
      text: this.venue.zones[key]?.label ?? key,
      position: new THREE.Vector3(v.x / v.n + 1.2, 2.6, v.z / v.n + 0.9),
    }));

    this.paint();
  }

  setHighlight(zone: string) {
    this.highlight = zone || '';
    this.paint();
  }

  /** Selected block lifts 1.35x and takes the accent; everything off-zone dims. */
  paint() {
    const c = this.colors;
    for (const mesh of this.meshes) {
      const b = mesh.userData as VenueBlock;
      const isSel = this.selected === mesh;
      const dim = !!this.highlight && b.zone !== this.highlight && !isSel;
      const mat = mesh.material as THREE.MeshStandardMaterial;
      const edge = this.edges.get(mesh);

      mat.color.setHex(isSel ? c.accent : (this.venue.zones[b.zone]?.color ?? c.edge));
      mat.emissive.setHex(isSel ? c.accentDeep : 0x000000);
      mat.opacity = dim ? 0.32 : 1;
      mat.transparent = dim;

      mesh.scale.y = isSel ? 1.35 : 1;
      mesh.position.y = (b.h / 2) * mesh.scale.y;

      if (edge) {
        const em = edge.material as THREE.LineBasicMaterial;
        em.color.setHex(isSel ? c.accentLight : c.edge);
        em.opacity = dim ? 0.15 : isSel ? 1 : 0.55;
        edge.scale.y = mesh.scale.y;
        edge.position.y = mesh.position.y;
      }
    }
  }

  labelOpacity(key: string): number {
    return this.highlight && key !== this.highlight ? 0.25 : 1;
  }

  /** Normalised device coords in, block out. Returns null on a miss. */
  hitTest(ndcX: number, ndcY: number): VenueBlock | null {
    this.raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), this.camera);
    const hit = this.raycaster.intersectObjects(this.meshes, false)[0];
    this.selected = hit ? (hit.object as THREE.Mesh) : null;
    this.paint();
    if (!hit) return null;
    const b = hit.object.userData as VenueBlock;
    return { ...b };
  }

  selectById(id: string | null) {
    this.selected = id ? this.meshes.find((m) => (m.userData as VenueBlock).id === id) ?? null : null;
    this.paint();
  }

  zoneLabelFor(block: VenueBlock): string {
    return this.venue.zones[block.zone]?.label ?? block.zone;
  }

  orbit(dx: number, dy: number) {
    this.rig.az -= dx * 0.006;
    this.rig.pol = clamp(this.rig.pol - dy * 0.005, POL_MIN, POL_MAX);
  }

  zoom(delta: number) {
    this.rig.dist = clamp(this.rig.dist + delta, DIST_MIN, DIST_MAX);
  }

  zoomByRatio(ratio: number) {
    this.rig.dist = clamp(this.rig.dist * ratio, DIST_MIN, DIST_MAX);
  }

  resize(width: number, height: number) {
    this.camera.aspect = width / Math.max(1, height);
    this.camera.updateProjectionMatrix();
  }

  /** Call once per frame before rendering. */
  updateCamera() {
    const { az, pol, dist } = this.rig;
    this.camera.position.set(
      Math.sin(az) * Math.cos(pol) * dist,
      Math.sin(pol) * dist + 2,
      Math.cos(az) * Math.cos(pol) * dist,
    );
    this.camera.lookAt(1, 1.0, -0.6);
  }

  /** Screen-space positions for the zone labels, in CSS px for the given box. */
  projectLabels(width: number, height: number): ProjectedLabel[] {
    return this.zoneLabels.map((z) => {
      const v = z.position.clone().project(this.camera);
      return {
        key: z.key,
        text: z.text,
        x: ((v.x + 1) / 2) * width,
        y: ((-v.y + 1) / 2) * height,
        visible: v.z <= 1,
      };
    });
  }

  dispose() {
    while (this.world.children.length) {
      const child = this.world.children[0];
      this.world.remove(child);
      disposeDeep(child);
    }
  }
}

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

function disposeDeep(obj: THREE.Object3D) {
  const anyObj = obj as unknown as {
    geometry?: THREE.BufferGeometry;
    material?: THREE.Material | THREE.Material[];
  };
  anyObj.geometry?.dispose?.();
  const mat = anyObj.material;
  if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
  else mat?.dispose?.();
}
